import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { 
  ArticleForAdjustment, 
  AdjustmentFilters, 
  AdjustmentResponse,
  AdjustmentReason 
} from '../interfaces';

interface AdjustmentsState {
  articles: ArticleForAdjustment[];
  selectedArticles: number[];
  filters: AdjustmentFilters;
  pagination: {
    total_elements: number;
    total_pages: number;
    page: number;
    size: number;
  } | null;
  loading: boolean;
  error: string | null;
  adjustmentReasons: AdjustmentReason[];
  providers: any[];
  lastAdjustmentResult: {
    success: boolean;
    message: string;
    adjusted_articles: number;
    total_quantity_adjusted: number;
  } | null;
}

const initialState: AdjustmentsState = {
  articles: [],
  selectedArticles: [],
  filters: {
    page: 1,
    size: 15,
  },
  pagination: null,
  loading: false,
  error: null,
  adjustmentReasons: [],
  providers: [],
  lastAdjustmentResult: null,
};

const adjustmentsSlice = createSlice({
  name: 'adjustments',
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setArticles: (state, action: PayloadAction<ArticleForAdjustment[]>) => {
      state.articles = action.payload;
    },
    setPagination: (state, action: PayloadAction<AdjustmentResponse>) => {
      state.pagination = {
        total_elements: action.payload.total_elements,
        total_pages: action.payload.total_pages,
        page: action.payload.page,
        size: action.payload.size,
      };
    },
    setFilters: (state, action: PayloadAction<Partial<AdjustmentFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    selectArticle: (state, action: PayloadAction<number>) => {
      if (!state.selectedArticles.includes(action.payload)) {
        state.selectedArticles.push(action.payload);
      }
    },
    unselectArticle: (state, action: PayloadAction<number>) => {
      state.selectedArticles = state.selectedArticles.filter(
        id => id !== action.payload
      );
    },
    selectAllArticles: (state) => {
      state.selectedArticles = state.articles.map(article => article.id);
    },
    clearSelection: (state) => {
      state.selectedArticles = [];
    },
    setAdjustmentReasons: (state, action: PayloadAction<AdjustmentReason[]>) => {
      state.adjustmentReasons = action.payload;
    },
    setProviders: (state, action: PayloadAction<any[]>) => {
      state.providers = action.payload;
    },
    setLastAdjustmentResult: (state, action: PayloadAction<any>) => {
      state.lastAdjustmentResult = action.payload;
    },
    clearLastAdjustmentResult: (state) => {
      state.lastAdjustmentResult = null;
    },
  },
});

export const {
  setLoading,
  setError,
  setArticles,
  setPagination,
  setFilters,
  selectArticle,
  unselectArticle,
  selectAllArticles,
  clearSelection,
  setAdjustmentReasons,
  setProviders,
  setLastAdjustmentResult,
  clearLastAdjustmentResult,
} = adjustmentsSlice.actions;

export default adjustmentsSlice.reducer;
